import * as React from 'react';
import type { Anchor } from '@/lib/drip';
import { cn } from '@/lib/cn';

/**
 * Decomposed offset_minutes into a friendly UI: direction + amount + unit.
 * Adapted from adcare-pl marketing/campaigns/[id]/_components/timing-picker.tsx.
 *
 * Controlled component: emits `{ offset_minutes, time_of_day }` upward.
 */

const ANCHOR_LABEL: Record<Anchor, string> = {
  booking_start: 'początku wizyty',
  booking_end: 'końca wizyty',
  campaign_start: 'startu kampanii',
  prev_step: 'poprzedniego kroku',
  last_visit: 'ostatniej wizyty pacjenta',
};

type Direction = 'instant' | 'before' | 'after';
type Unit = 'minutes' | 'hours' | 'days';

const UNIT_MIN: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440 };
const UNIT_LABEL: Record<Unit, string> = { minutes: 'min', hours: 'godz.', days: 'dni' };

function decompose(offsetMinutes: number): { direction: Direction; amount: number; unit: Unit } {
  if (offsetMinutes === 0) return { direction: 'instant', amount: 0, unit: 'minutes' };
  const direction: Direction = offsetMinutes < 0 ? 'before' : 'after';
  const abs = Math.abs(offsetMinutes);
  if (abs % 1440 === 0) return { direction, amount: abs / 1440, unit: 'days' };
  if (abs % 60 === 0) return { direction, amount: abs / 60, unit: 'hours' };
  return { direction, amount: abs, unit: 'minutes' };
}

function compose(direction: Direction, amount: number, unit: Unit): number {
  if (direction === 'instant') return 0;
  const sign = direction === 'before' ? -1 : 1;
  return sign * amount * UNIT_MIN[unit];
}

export interface TimingPickerProps {
  anchor: Anchor;
  offsetMinutes: number;
  timeOfDay: string | null;
  onChange: (next: { offset_minutes: number; time_of_day: string | null }) => void;
}

export function TimingPicker({ anchor, offsetMinutes, timeOfDay, onChange }: TimingPickerProps) {
  const decomposed = decompose(offsetMinutes);
  const [direction, setDirection] = React.useState<Direction>(decomposed.direction);
  const [amount, setAmount] = React.useState(decomposed.amount);
  const [unit, setUnit] = React.useState<Unit>(decomposed.unit);
  const [hasTime, setHasTime] = React.useState(!!timeOfDay);

  React.useEffect(() => {
    const d = decompose(offsetMinutes);
    setDirection(d.direction);
    setAmount(d.amount);
    setUnit(d.unit);
    setHasTime(!!timeOfDay);
  }, [offsetMinutes, timeOfDay]);

  const update = (nextDir: Direction, nextAmt: number, nextUnit: Unit, nextTime: string | null) => {
    onChange({
      offset_minutes: compose(nextDir, nextAmt, nextUnit),
      time_of_day: nextTime,
    });
  };

  const preview = (() => {
    if (direction === 'instant') return `W momencie ${ANCHOR_LABEL[anchor]}.`;
    const label = `${amount} ${UNIT_LABEL[unit]} ${direction === 'before' ? 'przed' : 'po'} ${ANCHOR_LABEL[anchor]}`;
    if (hasTime && timeOfDay) return `${label}, o godzinie ${timeOfDay}.`;
    return `${label}.`;
  })();

  return (
    <div className="space-y-3 rounded-md bg-surface p-4 ring-1 ring-black/5">
      <div className="grid grid-cols-3 gap-2">
        <select
          value={direction}
          onChange={(e) => {
            const d = e.target.value as Direction;
            setDirection(d);
            update(d, amount, unit, hasTime ? timeOfDay : null);
          }}
          className="h-10 rounded-md bg-bg px-3 text-sm ring-1 ring-border focus:outline-2 focus:-outline-offset-2 focus:outline-accent"
        >
          <option value="instant">w momencie</option>
          <option value="before">przed</option>
          <option value="after">po</option>
        </select>
        <input
          type="number"
          min={0}
          max={365}
          value={amount}
          disabled={direction === 'instant'}
          onChange={(e) => {
            const n = Math.max(0, Math.min(365, Number(e.target.value || 0)));
            setAmount(n);
            update(direction, n, unit, hasTime ? timeOfDay : null);
          }}
          className={cn(
            'h-10 rounded-md bg-bg px-3 text-sm tabular-nums ring-1 ring-border focus:outline-2 focus:-outline-offset-2 focus:outline-accent',
            direction === 'instant' && 'opacity-50'
          )}
        />
        <select
          value={unit}
          disabled={direction === 'instant'}
          onChange={(e) => {
            const u = e.target.value as Unit;
            setUnit(u);
            update(direction, amount, u, hasTime ? timeOfDay : null);
          }}
          className={cn(
            'h-10 rounded-md bg-bg px-3 text-sm ring-1 ring-border focus:outline-2 focus:-outline-offset-2 focus:outline-accent',
            direction === 'instant' && 'opacity-50'
          )}
        >
          <option value="minutes">{UNIT_LABEL.minutes}</option>
          <option value="hours">{UNIT_LABEL.hours}</option>
          <option value="days">{UNIT_LABEL.days}</option>
        </select>
      </div>
      <label className="flex items-center gap-2 text-xs text-text">
        <input
          type="checkbox"
          checked={hasTime}
          onChange={(e) => {
            const on = e.target.checked;
            setHasTime(on);
            update(direction, amount, unit, on ? (timeOfDay ?? '09:00') : null);
          }}
          className="size-4 rounded border-border text-accent focus:ring-accent"
        />
        Wyślij o określonej godzinie (zamiast „w momencie")
      </label>
      {hasTime ? (
        <input
          type="time"
          value={timeOfDay ?? '09:00'}
          onChange={(e) => {
            update(direction, amount, unit, e.target.value);
          }}
          className="h-10 w-32 rounded-md bg-bg px-3 text-sm ring-1 ring-border focus:outline-2 focus:-outline-offset-2 focus:outline-accent"
        />
      ) : null}
      <p className="text-xs text-text-muted italic">{preview}</p>
    </div>
  );
}
